// src/lib/campaigns/types.ts
//
// Types for the BENCHMARK partner-marketing campaigns module.
//
// Drives Agent B (Marketing Plan) — see
// docs/proposals/agent-runtime-comparison.md (Plan B) and the
// emarketing skill at ~/.hermes/skills/domain/emarketing/ for the
// 12 playbook items this module maps onto.
//
// Every campaign is: a play-template + an audience tier + a
// step-by-step delivery schedule. HITL-gated at every step.

import type { PricingTier } from "@/lib/demoData";

// The 12 emarketing plays that map cleanly onto a partner-tier
// campaign object. 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12 are
// exactly the SKILL.md enumeration.
//
// Plays NOT modeled as campaigns (from the skill's anti-pattern
// list):
//   - "TikTok ads"        (Play 13 is the agent's anti-pattern — no)
//   - "Crypto loyalty"    (anti-pattern — no)
// The remaining 12 are split between 1..4 (tier-segmented drips)
// and 5..12 (one-off + co-marketing).
export type CampaignPlayKey =
  | "play-1-tier-drip"
  | "play-2-cart-abandon"
  | "play-3-back-in-stock"
  | "play-4-brand-story"
  | "play-5-co-marketing"
  | "play-6-event-loop"
  | "play-7-comparison-seo"
  | "play-8-tier-gated-drop"
  | "play-9-vip-at-risk"
  | "play-10-install-referral"
  | "play-11-post-install-drip"
  | "play-12-qbr-deck";

export const CAMPAIGN_PLAY_LABEL: Record<CampaignPlayKey, string> = {
  "play-1-tier-drip": "Tier-segmented email journey",
  "play-2-cart-abandon": "Abandoned-cart outreach",
  "play-3-back-in-stock": "Watched-SKU back-in-stock alert",
  "play-4-brand-story": "Brand-story content",
  "play-5-co-marketing": "Co-marketing with partner shops",
  "play-6-event-loop": "Trade-show content loop",
  "play-7-comparison-seo": "Comparison SEO",
  "play-8-tier-gated-drop": "Tier-gated brand drop",
  "play-9-vip-at-risk": "VIP-at-risk retention",
  "play-10-install-referral": "Install-pro referral",
  "play-11-post-install-drip": "Post-install content drip",
  "play-12-qbr-deck": "Quarterly business-review deck",
};

export const CAMPAIGN_PLAY_SKILL_NUMBER: Record<CampaignPlayKey, number> = {
  "play-1-tier-drip": 1,
  "play-2-cart-abandon": 2,
  "play-3-back-in-stock": 3,
  "play-4-brand-story": 4,
  "play-5-co-marketing": 5,
  "play-6-event-loop": 6,
  "play-7-comparison-seo": 7,
  "play-8-tier-gated-drop": 8,
  "play-9-vip-at-risk": 9,
  "play-10-install-referral": 10,
  "play-11-post-install-drip": 11,
  "play-12-qbr-deck": 12,
};

// When the campaign is sent. Currently we only model first-touch
// fire time; subsequent steps in the same play carry a default
// +N day offset baked into the email body (e.g. Play 11 "Day 0 /
// Day 3 / Day 7"). v1 keeps the schedule flat — drop and refine.
export type CampaignStepKind =
  | "email"
  | "call-task"
  | "blog-draft"
  | "sms";

export interface CampaignStep {
  kind: CampaignStepKind;
  /** Free-form subject / call script snippet. */
  subject: string;
  /** Body preview — short, what the agent will queue. */
  body: string;
  /** Day offset from fireAt. Always 0 in v1. */
  dayOffset: number;
}

export type CampaignStatus =
  | "draft"
  | "scheduled"
  | "sending"
  | "complete"
  | "cancelled";

export interface Campaign {
  id: string;
  name: string;
  playKey: CampaignPlayKey;
  audienceTier: PricingTier | "all";
  status: CampaignStatus;
  /** ISO datetime, with no tz suffix. */
  fireAt: string;
  steps: CampaignStep[];
  createdAt: string;
  /** Audit trail — same shape as the other stores. */
  timeline: Array<{
    ts: string;
    status: CampaignStatus;
    actor: "system" | "agent" | "human";
    note: string;
  }>;
}

export const CAMPAIGN_STATUS_LABEL: Record<CampaignStatus, string> = {
  draft: "Draft",
  scheduled: "Scheduled",
  sending: "Sending",
  complete: "Complete",
  cancelled: "Cancelled",
};

export const CAMPAIGN_STATUS_TONE: Record<CampaignStatus, string> = {
  draft: "bg-bg-elevated text-fg-secondary",
  scheduled: "bg-accent-info/15 text-accent-info",
  sending: "bg-benchmark-teal/15 text-benchmark-teal-bright",
  complete: "bg-accent-green/15 text-accent-green",
  cancelled: "bg-accent-rose/15 text-accent-rose",
};
